import styles from "./ElevatorLevelSelect.module.css";
import {
  CreateButtonSvg,
  CustomTextSvg,
  EightSvg,
  ElevatorBackplate,
  ElevatorButtonDown,
  ElevatorButtonSvg,
  ElevatorButtonUp,
  FiveSvg,
  FloorTextSvg,
  FourSvg,
  JobTitle_CeoSvg,
  JobTitle_DirectorSvg,
  JobTitle_InternSvg,
  JobTitle_JuniorSvg,
  NineSvg,
  OneSvg,
  SevenSvg,
  SixSvg,
  SmallCheckmarkSvg,
  ThreeSvg,
  TitleButtonSvg,
  TwoSvg,
} from "./ElevatorLevelSelectSvgs";
import { useState } from "react";
import { useRecoilValue } from "recoil";
import { completedLevelsSelector } from "../../atoms/completedLevelsAtom";

const F_CUSTOM = "F_CUSTOM";
const F1 = "F1";
const F2 = "F2";
const F3 = "F3";
const F4 = "F4";
const F5 = "F5";

const FLOORS = [F5, F4, F3, F2, F1, F_CUSTOM];

const BUTTONS_PIXEL_SIZE_NUDGE_LEFT = 4;
const BUTTONS_PIXEL_SIZE_NUDGE_TOP = 20;

const getInitialFloor = (currentLevel) => {
  if (currentLevel.startsWith("1")) {
    return F1;
  }
  if (currentLevel.startsWith("2")) {
    return F2;
  }
  if (currentLevel.startsWith("3")) {
    return F3;
  }
  if (currentLevel.startsWith("4")) {
    return F4;
  }
  if (currentLevel.startsWith("5")) {
    return F5;
  }
  return F_CUSTOM;
};

const renderJobTitle = (progressPercentNumber) => {
  if (progressPercentNumber >= 100) {
    // eslint-disable-next-line react/jsx-pascal-case
    return <JobTitle_CeoSvg />;
  }

  if (progressPercentNumber >= 50) {
    // eslint-disable-next-line react/jsx-pascal-case
    return <JobTitle_DirectorSvg />;
  }

  if (progressPercentNumber >= 25) {
    // eslint-disable-next-line react/jsx-pascal-case
    return <JobTitle_JuniorSvg />;
  }

  // eslint-disable-next-line react/jsx-pascal-case
  return <JobTitle_InternSvg />;
};

const renderFloorTitle = (floor) => {
  if (floor === F_CUSTOM) {
    return <CustomTextSvg />;
  }

  return (
    <div
      style={{
        display: "flex",
        gap: "calc(2px * var(--elevator-menu-pixel-size)",
      }}
    >
      <FloorTextSvg />
      {floor === F5 && <FiveSvg />}
      {floor === F4 && <FourSvg />}
      {floor === F3 && <ThreeSvg />}
      {floor === F2 && <TwoSvg />}
      {floor === F1 && <OneSvg />}
    </div>
  );
};

const renderElevatorMinimap = (floor = "") => {
  return (
    <svg
      style={{
        display: "block",
        width: `calc(1px * var(--elevator-menu-pixel-size)`,
      }}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 -0.5 1 12"
      shapeRendering="crispEdges"
    >
      <path stroke={floor === F5 ? "#3baaff" : "#000"} d="M0 0h1" />
      <path stroke={floor === F4 ? "#3baaff" : "#000"} d="M0 2h1" />
      <path stroke={floor === F3 ? "#3baaff" : "#000"} d="M0 4h1" />
      <path stroke={floor === F2 ? "#3baaff" : "#000"} d="M0 6h1" />
      <path stroke={floor === F1 ? "#3baaff" : "#000"} d="M0 8h1" />
      <path stroke={floor === F_CUSTOM ? "#fff" : "#000"} d="M0 11h1" />
    </svg>
  );
};

const elevatorButtonPositions = [
  { levelNumber: 1, x: 0, y: 0, numberSvg: OneSvg },
  { levelNumber: 2, x: 17, y: 0, numberSvg: TwoSvg },
  { levelNumber: 3, x: 34, y: 0, numberSvg: ThreeSvg },
  { levelNumber: 4, x: 0, y: 17, numberSvg: FourSvg },
  { levelNumber: 5, x: 17, y: 17, numberSvg: FiveSvg },
  { levelNumber: 6, x: 34, y: 17, numberSvg: SixSvg },
  { levelNumber: 7, x: 0, y: 34, numberSvg: SevenSvg },
  { levelNumber: 8, x: 17, y: 34, numberSvg: EightSvg },
  { levelNumber: 9, x: 34, y: 34, numberSvg: NineSvg },
];

const renderPossibleElevatorGrid = (
  currentFloor,
  currentLevel,
  onLoadStoryLevel,
  completedLevels
) => {
  if (currentFloor === F_CUSTOM) {
    return null;
  }

  return elevatorButtonPositions.map((b) => {
    const mappedFloorLevelCombo =
      currentFloor.replace("F", "") + "-" + b.levelNumber;

    const isCompleted = completedLevels[mappedFloorLevelCombo];
    return (
      <button
        onClick={() => {
          onLoadStoryLevel(mappedFloorLevelCombo);
        }}
        key={`${b.x}x${b.y}`}
        style={{
          position: "absolute",
          left: `calc(${
            b.x + BUTTONS_PIXEL_SIZE_NUDGE_LEFT
          }px * var(--elevator-menu-pixel-size)`,
          top: `calc(${
            b.y + BUTTONS_PIXEL_SIZE_NUDGE_TOP
          }px * var(--elevator-menu-pixel-size)`,
        }}
      >
        <ElevatorButtonSvg
          floor={currentFloor}
          isActive={currentLevel === mappedFloorLevelCombo}
        />
        <div
          style={{
            position: "absolute",
            left: `calc(4px * var(--elevator-menu-pixel-size)`,
            top: `calc(3px * var(--elevator-menu-pixel-size)`,
          }}
        >
          {b.numberSvg()}
        </div>
        {isCompleted && (
          <div
            style={{
              position: "absolute",
              left: `calc(9px * var(--elevator-menu-pixel-size)`,
              top: `calc(9px * var(--elevator-menu-pixel-size)`,
            }}
          >
            <SmallCheckmarkSvg />
          </div>
        )}
      </button>
    );
  });
};

const renderPossibleCustomLevelBox = (
  customLevels,
  onLoadStoryLevel,
  onDeleteLevel
) => {
  return (
    <div
      style={{
        position: "absolute",
        left: `calc(3px * var(--elevator-menu-pixel-size)`,
        right: `calc(3px * var(--elevator-menu-pixel-size)`,
        top: `calc(19px * var(--elevator-menu-pixel-size)`,
        height: `calc(49px * var(--elevator-menu-pixel-size)`,
        overflowY: "scroll",
        borderTop: `calc(1px * var(--elevator-menu-pixel-size)) solid #000`,
        background: "rgb(37, 31, 39)",
      }}
    >
      {customLevels.map((levelData, index) => {
        return (
          <div
            key={index}
            style={{
              color: "#fff",
              display: "flex",
              justifyContent: "space-between",
              padding: "0.5rem",
            }}
          >
            <button
              onClick={() => {
                onLoadStoryLevel(levelData.key);
              }}
            >
              {levelData.title}
            </button>
            <button
              onClick={() => {
                onDeleteLevel(levelData.key);
              }}
              style={{ color: "red" }}
            >
              Delete
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default function ElevatorLevelSelect({
  customLevels,
  currentLevel,
  onLoadLevel,
  onDeleteLevel,
  onCreateNewLevel,
  onBackToTitle,
}) {
  const [currentFloor, setCurrentFloor] = useState(
    getInitialFloor(currentLevel)
  );
  const completedLevels = useRecoilValue(completedLevelsSelector);

  const changeFloorUp = () => {
    const nextIndex = FLOORS.indexOf(currentFloor) - 1;
    if (nextIndex > -1) {
      setCurrentFloor(FLOORS[nextIndex]);
    }
  };

  const changeFloorDown = () => {
    const nextIndex = FLOORS.indexOf(currentFloor) + 1;
    if (nextIndex < FLOORS.length) {
      setCurrentFloor(FLOORS[nextIndex]);
    }
  };

  const progressPercentNumber =
    (Object.keys(completedLevels).length / (5 * 9)) * 100;

  return (
    <div className={styles.elevatorMenu}>
      <ElevatorBackplate />
      <div
        style={{
          position: "absolute",
          left: `calc(1px * var(--elevator-menu-pixel-size)`,
          top: `calc(1px * var(--elevator-menu-pixel-size)`,
          width: `calc(56px * var(--elevator-menu-pixel-size)`,
          height: `calc(22px * var(--elevator-menu-pixel-size)`,
          // background: "green",
        }}
      >
        {/* Job title container */}
        <div
          style={{
            position: "absolute",
            left: `calc(22px * var(--elevator-menu-pixel-size)`,
            top: `calc(9px * var(--elevator-menu-pixel-size)`,
          }}
        >
          {renderJobTitle(progressPercentNumber)}
        </div>

        {/* Progress container */}
        <div
          style={{
            position: "absolute",
            // background: "orange",
            left: `calc(22px * var(--elevator-menu-pixel-size)`,
            top: `calc(16px * var(--elevator-menu-pixel-size)`,
            width: `calc(32px * var(--elevator-menu-pixel-size)`,
            height: `calc(2px * var(--elevator-menu-pixel-size)`,
          }}
        >
          {/* Fill */}
          <div
            style={{
              position: "absolute",
              // background: "orange",
              left: 0,
              top: 0,
              width: progressPercentNumber + "%",
              bottom: 0,
              borderTop:
                "calc(1px * var(--elevator-menu-pixel-size)) solid #8cff9b",
              background: "#00f220",
            }}
          ></div>
        </div>
      </div>
      <div
        style={{
          position: "absolute",
          left: `calc(1px * var(--elevator-menu-pixel-size)`,
          top: `calc(24px * var(--elevator-menu-pixel-size)`,
          width: `calc(56px * var(--elevator-menu-pixel-size)`,
          height: `calc(99px * var(--elevator-menu-pixel-size)`,
          // background: "green",
        }}
      >
        {/* Floor name */}
        <div
          style={{
            position: "absolute",
            left: `calc(4px * var(--elevator-menu-pixel-size)`,
            top: `calc(6px * var(--elevator-menu-pixel-size)`,
          }}
        >
          {renderFloorTitle(currentFloor)}
        </div>

        {/* Up/Down buttons */}
        <button
          onClick={changeFloorUp}
          style={{
            position: "absolute",
            left: `calc(41px * var(--elevator-menu-pixel-size)`,
            top: `calc(3px * var(--elevator-menu-pixel-size)`,
            opacity: currentFloor === F5 ? "0.1" : "1",
          }}
        >
          <ElevatorButtonUp />
        </button>
        <button
          onClick={changeFloorDown}
          style={{
            position: "absolute",
            left: `calc(41px * var(--elevator-menu-pixel-size)`,
            top: `calc(11px * var(--elevator-menu-pixel-size)`,
            opacity: currentFloor === F_CUSTOM ? "0.1" : "1",
          }}
        >
          <ElevatorButtonDown />
        </button>

        {/* Progress indicator */}
        <div
          style={{
            position: "absolute",
            left: `calc(52px * var(--elevator-menu-pixel-size)`,
            top: `calc(4px * var(--elevator-menu-pixel-size)`,
          }}
        >
          {renderElevatorMinimap(currentFloor)}
        </div>

        {/* Nine floor buttons */}
        {renderPossibleElevatorGrid(
          currentFloor,
          currentLevel,
          onLoadLevel,
          completedLevels
        )}

        {currentFloor === F_CUSTOM &&
          renderPossibleCustomLevelBox(
            customLevels,
            onLoadLevel,
            onDeleteLevel
          )}

        {/* Bottom buttons */}
        <button
          onClick={onCreateNewLevel}
          style={{
            position: "absolute",
            left: `calc(4px * var(--elevator-menu-pixel-size)`,
            top: `calc(71px * var(--elevator-menu-pixel-size)`,
          }}
        >
          <CreateButtonSvg />
        </button>
        <button
          onClick={onBackToTitle}
          style={{
            position: "absolute",
            left: `calc(4px * var(--elevator-menu-pixel-size)`,
            top: `calc(84px * var(--elevator-menu-pixel-size)`,
          }}
        >
          <TitleButtonSvg />
        </button>
      </div>
    </div>
  );
}
